import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { trackEvent, PlausibleEvent } from "./plausible";

// H4 — first-workout sentinel. We mark localStorage on the first
// useComplete success per browser; activation funnel uses this to
// distinguish "registered" → "actually used the product". Per-browser
// is good enough — Plausible cookieless treats devices as separate
// anyway.
const FIRST_WORKOUT_KEY = "storica:first_workout_tracked";

export type WorkoutItem = {
  id: number;
  scheduled_date: string;
  discipline: string | null;
  title: string | null;
  description: string | null;
  planned_duration_minutes: number | null;
  planned_distance_km: number | null;
  planned_load?: number | null;
  /** W-REBUILD-2 — фактическая нагрузка выполненной тренировки. */
  actual_load?: number | null;
  is_key_workout?: boolean;
  /**
   * Самая высокая зона в структуре работы — прямое указание её автора.
   *
   * Единственный признак тяжести, который у импортированного плана заполнен:
   * `is_key_workout`, `intensity_factor` и `planned_load` там пусты все до
   * одного (замер на проде 2026-08-04).
   */
  max_zone?: number | null;
  actual_distance_km: number | null;
  actual_duration_minutes: number | null;
  status: string;
  rpe: number | null;
  feel: string | null;
  athlete_notes: string | null;
  intent?: string | null;
};

export type Plan = {
  from_date: string;
  to_date: string;
  items: WorkoutItem[];
  pending_weeks?: string[];
};

export function usePlan(from: string, to: string, opts?: { enabled?: boolean }) {
  return useQuery<Plan>({
    queryKey: ["plan", from, to],
    queryFn: async () =>
      (await api.get<Plan>(`/athlete/plan`, { params: { from, to } })).data,
    staleTime: 30_000,
    enabled: opts?.enabled ?? true,
  });
}

// ── Plan phases (WAVE 2.2 PhaseStrip) ─────────────────────────

export interface PlanPhaseItem {
  name: string;
  start_week_index: number;
  end_week_index: number;
  volume_pct: number;
  intensity_pct: number;
  start_date: string;
  end_date: string;
}

export interface PlanPhasesResp {
  week_anchor: string;
  phases: PlanPhaseItem[];
}

export function usePlanPhases(weekAnchor?: string) {
  return useQuery<PlanPhasesResp>({
    queryKey: ["plan-phases", weekAnchor ?? "current"],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (weekAnchor) params.week_anchor = weekAnchor;
      const r = await api.get<PlanPhasesResp>("/athlete/plan/phases", { params });
      return r.data;
    },
    staleTime: 5 * 60_000,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["plan"] });
  qc.invalidateQueries({ queryKey: ["dashboard"] });
  // ["workout", id] — деталь тренировки: после complete/reschedule/cancel
  // WorkoutDetailPage показывал stale-статус, т.к. его кэш не сбрасывался.
  qc.invalidateQueries({ queryKey: ["workout"] });
}

export function useComplete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: number; rpe?: number; feel?: string; notes?: string }) => {
      const { id, ...body } = vars;
      const r = await api.post(`/athlete/workouts/${id}/complete`, body);
      return r.data;
    },
    onSuccess: () => {
      try {
        if (!localStorage.getItem(FIRST_WORKOUT_KEY)) {
          trackEvent(PlausibleEvent.FirstWorkoutLogged);
          localStorage.setItem(FIRST_WORKOUT_KEY, "1");
        }
      } catch {
        // silent: localStorage unavailable (private mode / quota)
      }
      invalidateAll(qc);
    },
  });
}

export function useReschedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: number; new_date: string }) => {
      const r = await api.post(`/athlete/workouts/${vars.id}/reschedule`, { new_date: vars.new_date });
      return r.data;
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export interface PhoneRecordVars {
  discipline: string;
  scheduled_date?: string;
  actual_duration_minutes: number;
  actual_distance_km?: number | null;
  avg_pace_s_per_km?: number | null;
  avg_hr?: number | null;
  max_hr?: number | null;
  /** Чем снят пульс: "ble" — внешний датчик был подключён в этой сессии. */
  hr_sensor_type?: "chest" | "ble" | "ohr" | "phone" | null;
  elevation_gain_m?: number | null;
  rpe?: number | null;
  feel?: string | null;
  notes?: string | null;
  planned_workout_id?: number | null;
  raw_streams?: Array<{ type: string; data: number[] }> | null;
}

export interface PhoneRecordResult {
  id: number;
  status: string;
  source: string;
  matched: string;
  discipline: string;
  tss_quality?: string;
}

/** Persist a phone-recorded activity as a completed workout (source='phone'). */
export function useRecordPhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: PhoneRecordVars) => {
      const r = await api.post<PhoneRecordResult>("/athlete/workouts/phone-record", vars);
      return r.data;
    },
    onSuccess: () => {
      try {
        if (!localStorage.getItem(FIRST_WORKOUT_KEY)) {
          trackEvent(PlausibleEvent.FirstWorkoutLogged);
          localStorage.setItem(FIRST_WORKOUT_KEY, "1");
        }
      } catch {
        // silent: localStorage unavailable (private mode / quota)
      }
      invalidateAll(qc);
    },
  });
}

export function useCopy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: number; new_date: string }) => {
      const r = await api.post(`/athlete/workouts/${vars.id}/copy`, { new_date: vars.new_date });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plan"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}


/**
 * Повторить прошлую неделю в указанную.
 *
 * 409 `target_week_not_empty` — целевая неделя не пуста. Это не ошибка ввода,
 * а защита: без неё второе нажатие удвоило бы неделю, а отмены пакетного
 * создания у атлета нет. Экран показывает это отдельным сообщением.
 */
export function useCopyPreviousWeek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { target_week_start: string }) => {
      const r = await api.post<{ copied_ids: number[]; copied_count: number }>(
        "/athlete/plan/copy-previous-week",
        vars,
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plan"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}


export function useCancel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: number; reason?: string }) => {
      const r = await api.post(`/athlete/workouts/${vars.id}/cancel`, { reason: vars.reason });
      return r.data;
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useGenerateSelfPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<{ ok: boolean; workouts_created: number }>("/athlete/self-plan/generate");
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plan"] }),
  });
}
